import { useCallback, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { useGetNotifications } from '../hooks/backend/notifications'
import { useCapabilities } from '../hooks/useCapabilities'
import { cn } from '../lib/shadcn/utils'
import type { NotificationFeed } from '../utils/notifications'

// The Notifications button, at the top of every page.
//
// WHY IT POLLS RATHER THAN PUSHES. There is no socket in this application and
// adding one for a badge would be a large amount of machinery for a number.
// Every three minutes matches the send-outbound-messages job, so the badge and
// the email a person gets for the same mention arrive within the same window
// rather than telling them two different things.
//
// WHY THE COUNT COMES FROM THE SAME ENDPOINT AS THE LIST. One call returns both.
// A dedicated count endpoint would be a second round trip on every navigation
// for a number that is already in the rows, and two endpoints computing one
// number is how a badge comes to disagree with the page it opens.
const REFRESH_MS = 3 * 60 * 1000

export function NotificationsButton({ compact = false }: { compact?: boolean }) {
  const { canView } = useCapabilities()
  const { data, trigger } = useGetNotifications<NotificationFeed>()

  const load = useCallback(() => {
    // A small limit: the badge only needs to know whether there is anything, and
    // roughly how much. The page asks for the whole feed when it opens.
    void trigger({ limit: 200 }, { skipCache: true })
  }, [trigger])

  useEffect(() => {
    if (!canView) return
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => window.clearInterval(id)
  }, [canView, load])

  if (!canView) return null

  const feed = (data as NotificationFeed | undefined) ?? null
  const unread = feed?.unread ?? 0
  // 99 and not 100, because three digits in a badge that size is unreadable and
  // the exact number stops mattering long before then.
  const label = unread > 99 ? '99+' : String(unread)

  return (
    <NavLink
      to="/notifications"
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      title={unread > 0 ? `${unread} unread` : 'Notifications'}
      className={({ isActive }) =>
        cn(
          'relative inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium transition-colors',
          compact && 'px-2',
          isActive
            ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )
      }
    >
      <Bell className="h-4 w-4 shrink-0" />
      {!compact && <span>Notifications</span>}
      {unread > 0 && (
        <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[hsl(var(--brand))] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white">
          {label}
        </span>
      )}
    </NavLink>
  )
}
